import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Star, Heart, MessagesSquare, Users, CalendarClock, PhoneCall, GitCompare,
  StickyNote, ListChecks, MapPin, Briefcase, GraduationCap, Sparkles, MoreHorizontal,
  ShieldCheck, Clock, Plus, X, Check, FolderPlus, Trash2, Pencil, Bell, ChevronRight, BellRing,
  Search, SlidersHorizontal, Bookmark, BookmarkPlus, IdCard, RefreshCw, XCircle, CheckCircle2,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Avatar";
import { VerifiedBadges } from "@/components/VerifiedBadges";
import { FullSpinner, Empty } from "@/components/States";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const STAGE_LABEL = {
  shortlisted: "Shortlisted", interest_sent: "Interest Sent", interest_accepted: "Connected",
  chat_started: "Chatting", contact_requested: "Contact Requested", contact_approved: "Contact Shared",
  meeting_planned: "Meeting Planned", meeting_completed: "Met",
};
const FAMILY_FIELDS = [
  { key: "basic", label: "Basic profile" }, { key: "education", label: "Education" },
  { key: "career", label: "Career" }, { key: "family", label: "Family" },
  { key: "lifestyle", label: "Lifestyle" }, { key: "horoscope", label: "Horoscope" },
  { key: "compatibility", label: "Compatibility summary" }, { key: "photos", label: "Shared photos" },
];
const MEETING_TYPES = [
  { key: "coffee", label: "Coffee" }, { key: "lunch", label: "Lunch / Dinner" },
  { key: "mall", label: "Mall / Public place" }, { key: "family", label: "Family meeting" },
  { key: "video", label: "Video call meeting" },
];

const refreshAttention = () => window.dispatchEvent(new Event("mj:refresh-attention"));

function timeAgo(iso) {
  if (!iso) return null;
  const diff = (Date.now() - new Date(iso).getTime()) / 60000;
  if (diff < 15) return "Online now";
  if (diff < 60) return "Active recently";
  if (diff < 1440) return `Active ${Math.floor(diff / 60)}h ago`;
  return `Active ${Math.floor(diff / 1440)}d ago`;
}

const TABS = [
  { key: "shortlisted", label: "Shortlisted", icon: Star },
  { key: "compare", label: "Compare", icon: GitCompare },
  { key: "conversations", label: "Conversations", icon: MessagesSquare },
  { key: "contact", label: "Contact Status", icon: PhoneCall },
  { key: "family", label: "Family Review", icon: Users },
  { key: "meetings", label: "Meetings", icon: CalendarClock },
  { key: "notes", label: "Notes & Reminders", icon: StickyNote },
  { key: "actions", label: "Next Actions", icon: ListChecks },
];

export default function DecisionWorkspace() {
  const [tab, setTab] = useState("shortlisted");
  const [summary, setSummary] = useState(null);
  const [insights, setInsights] = useState(null);
  const [showInsights, setShowInsights] = useState(true);

  const loadSummary = () => api.get("/jodi/workspace/summary").then(({ data }) => setSummary(data.cards)).catch(() => {});
  const loadInsights = () => api.get("/jodi/workspace/insights").then(({ data }) => setInsights(data)).catch(() => {});
  useEffect(() => { loadSummary(); loadInsights(); }, []);

  const cards = summary ? [
    { label: "Shortlisted", value: summary.shortlisted, icon: Star, tab: "shortlisted" },
    { label: "Awaiting Response", value: summary.awaiting_response, icon: Clock, tab: "contact" },
    { label: "Active Conversations", value: summary.active_conversations, icon: MessagesSquare, tab: "conversations" },
    { label: "Family Review", value: summary.family_review, icon: Users, tab: "family" },
    { label: "Meetings Planned", value: summary.meetings_planned, icon: CalendarClock, tab: "meetings" },
    { label: "Contact Requests", value: summary.contact_requests, icon: PhoneCall, tab: "contact" },
    { label: "Needs Attention", value: summary.needs_attention, icon: BellRing, tab: "actions", accent: true },
  ] : [];

  return (
    <div className="space-y-6" data-testid="decision-workspace">
      <header>
        <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#58111A]">My Jodi Workspace</h1>
        <p className="text-sm text-[#605255] mt-1 max-w-xl">Organize the profiles you are seriously considering and manage your next steps in one place.</p>
      </header>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="workspace-summary">
        {cards.map((c) => (
          <button key={c.label} onClick={() => setTab(c.tab)} data-testid={`summary-${c.tab}`}
            className={`text-left rounded-2xl p-4 border transition hover:-translate-y-0.5 ${c.accent && c.value > 0 ? "bg-[#FBEBE1] border-[#C59B27]" : "bg-white border-[#E8DFD1]"}`}>
            <c.icon className={`w-4 h-4 mb-2 ${c.accent && c.value > 0 ? "text-[#BE123C]" : "text-[#C59B27]"}`} />
            <div className="font-serif text-2xl font-bold text-[#58111A]">{c.value}</div>
            <div className="text-[11px] text-[#605255] leading-tight">{c.label}</div>
          </button>
        ))}
      </div>

      {/* Weekly insights */}
      {showInsights && insights && (insights.counts.needs_reply + insights.counts.pending_meetings + insights.counts.going_stale) > 0 && (
        <InsightsBanner insights={insights} onDismiss={() => setShowInsights(false)} onGoTab={setTab} />
      )}

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1 border-b border-[#E8DFD1]" data-testid="workspace-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
            className={`flex items-center gap-2 px-3.5 py-2 rounded-t-xl text-sm font-medium whitespace-nowrap transition border-b-2 ${tab === t.key ? "border-[#781826] text-[#781826]" : "border-transparent text-[#605255] hover:text-[#781826]"}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      <div>
        {tab === "shortlisted" && <ShortlistedTab onChange={loadSummary} />}
        {tab === "compare" && <CompareTab />}
        {tab === "conversations" && <ConversationsTab />}
        {tab === "contact" && <ContactStatusTab onChange={loadSummary} />}
        {tab === "family" && <FamilyTab onChange={loadSummary} />}
        {tab === "meetings" && <MeetingsTab onChange={loadSummary} />}
        {tab === "notes" && <NotesRemindersTab />}
        {tab === "actions" && <NextActionsTab onChange={loadSummary} />}
      </div>

      <p className="text-xs text-[#9E7B1B] text-center pt-4">Every decision is yours. ManaJodi organises facts and steps — it never decides for you.</p>
    </div>
  );
}

/* ------------------------------------------------------ Weekly insights */
function InsightsBanner({ insights, onDismiss, onGoTab }) {
  const nav = useNavigate();
  const groups = [
    { key: "needs_reply", title: "Needs a reply", icon: MessagesSquare, items: insights.needs_reply, tint: "text-[#BE123C]", tab: "actions" },
    { key: "pending_meetings", title: "Pending meetings", icon: CalendarClock, items: insights.pending_meetings, tint: "text-[#9E7B1B]", tab: "meetings" },
    { key: "going_stale", title: "Going quiet", icon: Clock, items: insights.going_stale, tint: "text-[#605255]", tab: "shortlisted" },
  ].filter((g) => g.items.length > 0);

  return (
    <div className="rounded-2xl border border-[#C59B27]/50 bg-gradient-to-br from-[#FDF6E9] to-[#FBEEE2] p-5" data-testid="workspace-insights">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#C59B27]" />
          <div>
            <h2 className="font-serif text-lg font-semibold text-[#58111A]">Your week at a glance</h2>
            <p className="text-[11px] text-[#9E7B1B]">A gentle nudge on where your attention helps most — you decide what to act on.</p>
          </div>
        </div>
        <button onClick={onDismiss} data-testid="dismiss-insights" className="text-[#9E7B1B] hover:text-[#781826]"><X className="w-4 h-4" /></button>
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        {groups.map((g) => (
          <div key={g.key} className="bg-white/70 rounded-xl p-3 border border-white" data-testid={`insight-${g.key}`}>
            <div className="flex items-center justify-between mb-2">
              <div className={`flex items-center gap-1.5 text-xs font-semibold ${g.tint}`}><g.icon className="w-3.5 h-3.5" /> {g.title}</div>
              <span className="text-[10px] font-bold bg-[#3B0A12] text-[#E2C382] rounded-full px-2 py-0.5">{g.items.length}</span>
            </div>
            <div className="space-y-1.5">
              {g.items.slice(0, 3).map((it) => (
                <button key={it.user_id} onClick={() => nav(`/app/profile/${it.user_id}`)} data-testid={`insight-item-${it.user_id}`}
                  className="w-full flex items-center gap-2 text-left hover:bg-[#FAF3E0] rounded-lg p-1 transition">
                  <Avatar photos={it.photos} name={it.full_name} size={28} className="rounded-full w-7 h-7 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-[12px] font-medium text-[#261A1C] truncate">{it.full_name}</div>
                    <div className="text-[10px] text-[#605255] truncate">{it.reason}</div>
                  </div>
                </button>
              ))}
            </div>
            <button onClick={() => onGoTab(g.tab)} className="mt-2 text-[11px] font-medium text-[#781826] hover:underline flex items-center gap-0.5">
              View all <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------ Shortlisted + collections + advanced search */
const STAGE_OPTS = [
  { v: "", l: "Any stage" }, { v: "shortlisted", l: "Shortlisted" }, { v: "interest_sent", l: "Interest sent" },
  { v: "interest_accepted", l: "Connected" }, { v: "chat_started", l: "Chatting" },
  { v: "contact_requested", l: "Contact requested" }, { v: "contact_approved", l: "Contact shared" },
  { v: "meeting_planned", l: "Meeting planned" },
];

function ShortlistedTab({ onChange }) {
  const [collections, setCollections] = useState([]);
  const [active, setActive] = useState("");
  const [data, setData] = useState(null);
  const [manage, setManage] = useState(false);
  const [filters, setFilters] = useState({ q: "", verified: false, min_score: "", stage: "", needs_attention: false });
  const [saved, setSaved] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [codeResult, setCodeResult] = useState(null);
  const [savingName, setSavingName] = useState(false);
  const pinAppliedRef = useRef(false);

  const loadCols = () => api.get("/jodi/collections").then(({ data }) => setCollections(data.collections)).catch(() => {});
  const loadSaved = () => api.get("/jodi/saved-searches").then(({ data }) => {
    setSaved(data.searches);
    const pin = data.searches.find((s) => s.pinned);
    if (pin && !pinAppliedRef.current) { pinAppliedRef.current = true; setFilters((f) => ({ ...f, ...pin.filters })); }
  }).catch(() => {});
  const load = () => {
    setData(null);
    const p = new URLSearchParams();
    if (active) p.set("collection", active);
    if (filters.q) p.set("q", filters.q);
    if (filters.verified) p.set("verified", "true");
    if (filters.min_score) p.set("min_score", filters.min_score);
    if (filters.stage) p.set("stage", filters.stage);
    if (filters.needs_attention) p.set("needs_attention", "true");
    api.get(`/jodi/workspace/profiles?${p.toString()}`).then(({ data }) => setData(data)).catch(() => setData({ results: [] }));
  };
  useEffect(() => { loadCols(); loadSaved(); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [active, filters]);

  const isCode = /^mj-?\w{4,}$/i.test(filters.q.trim());
  const findByCode = async () => {
    try { const { data } = await api.get(`/jodi/search/by-code/${filters.q.trim()}`); setCodeResult(data.profile); }
    catch (e) { toast.error(apiError(e)); }
  };
  const applySaved = (s) => setFilters({ q: "", verified: false, min_score: "", stage: "", needs_attention: false, ...s.filters });
  const saveCurrent = async (name) => {
    try { await api.post("/jodi/saved-searches", { name, filters }); setSavingName(false); loadSaved(); toast.success("Search saved"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const delSaved = async (id) => { try { await api.delete(`/jodi/saved-searches/${id}`); loadSaved(); } catch (e) { toast.error(apiError(e)); } };
  const pinSaved = async (id) => { try { await api.put(`/jodi/saved-searches/${id}/pin`); loadSaved(); } catch (e) { toast.error(apiError(e)); } };
  const activeFilterCount = [filters.verified, filters.min_score, filters.stage, filters.needs_attention].filter(Boolean).length;

  return (
    <div className="space-y-4">
      {/* Search + filter row */}
      <div className="flex flex-col sm:flex-row gap-2" data-testid="workspace-search">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9E7B1B]" />
          <input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            placeholder="Search by name, city, profession, or Profile ID (MJ-XXXXXX)" data-testid="ws-search-input"
            className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-[#E8DFD1] text-sm" />
        </div>
        {isCode && <button onClick={findByCode} data-testid="find-by-id-btn" className="btn-primary text-sm px-4 whitespace-nowrap"><IdCard className="w-4 h-4" /> Find by ID</button>}
        <button onClick={() => setShowFilters((s) => !s)} data-testid="toggle-filters-btn"
          className={`text-sm px-4 py-2.5 rounded-xl border flex items-center gap-2 ${activeFilterCount ? "border-[#781826] text-[#781826] bg-[#FBEBE1]" : "border-[#E8DFD1] text-[#605255]"}`}>
          <SlidersHorizontal className="w-4 h-4" /> Filters{activeFilterCount ? ` (${activeFilterCount})` : ""}
        </button>
      </div>

      {showFilters && (
        <div className="mj-card p-4 grid gap-3 sm:grid-cols-4" data-testid="filter-panel">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={filters.verified} onChange={(e) => setFilters((f) => ({ ...f, verified: e.target.checked }))} data-testid="filter-verified" className="w-4 h-4 accent-[#781826]" /> Verified only</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={filters.needs_attention} onChange={(e) => setFilters((f) => ({ ...f, needs_attention: e.target.checked }))} data-testid="filter-attention" className="w-4 h-4 accent-[#781826]" /> Needs attention</label>
          <select value={filters.stage} onChange={(e) => setFilters((f) => ({ ...f, stage: e.target.value }))} data-testid="filter-stage" className="border border-[#E8DFD1] rounded-lg px-2 py-2 text-sm">
            {STAGE_OPTS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
          <select value={filters.min_score} onChange={(e) => setFilters((f) => ({ ...f, min_score: e.target.value }))} data-testid="filter-score" className="border border-[#E8DFD1] rounded-lg px-2 py-2 text-sm">
            <option value="">Any compatibility</option><option value="60">60%+</option><option value="70">70%+</option><option value="80">80%+</option><option value="90">90%+</option>
          </select>
          <div className="sm:col-span-4 flex items-center gap-2 flex-wrap pt-1 border-t border-[#F1E9DA]">
            {savingName ? (
              <SaveSearchInline onSave={saveCurrent} onCancel={() => setSavingName(false)} />
            ) : (
              <button onClick={() => setSavingName(true)} data-testid="save-search-btn" className="text-xs px-3 py-1.5 rounded-full border border-dashed border-[#C59B27] text-[#9E7B1B] flex items-center gap-1"><BookmarkPlus className="w-3.5 h-3.5" /> Save this search</button>
            )}
            <button onClick={() => setFilters({ q: "", verified: false, min_score: "", stage: "", needs_attention: false })} className="text-xs px-3 py-1.5 rounded-full border border-[#E8DFD1] text-[#605255]">Clear</button>
          </div>
        </div>
      )}

      {saved.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="saved-searches">
          <span className="text-[11px] text-[#9E7B1B] flex items-center gap-1"><Bookmark className="w-3.5 h-3.5" /> Saved:</span>
          {saved.map((s) => (
            <span key={s.id} className={`group flex items-center gap-1 px-2.5 py-1 rounded-full text-xs ${s.pinned ? "bg-[#781826] text-white" : "bg-[#FAF3E0] text-[#9E7B1B]"}`}>
              <button onClick={() => pinSaved(s.id)} data-testid={`pin-${s.id}`} title={s.pinned ? "Unpin default" : "Pin as default"} className="opacity-70 hover:opacity-100">
                <Bookmark className="w-3 h-3" fill={s.pinned ? "currentColor" : "none"} />
              </button>
              <button onClick={() => applySaved(s)} data-testid={`saved-${s.id}`}>{s.name}</button>
              <button onClick={() => delSaved(s.id)} className="opacity-50 hover:opacity-100"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}

      {/* Collection chips */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="collection-bar">
        <button onClick={() => setActive("")} className={`px-3 py-1.5 rounded-full text-xs font-medium border ${active === "" ? "bg-[#781826] text-white border-[#781826]" : "border-[#E8DFD1] text-[#605255]"}`}>All ({data?.total ?? 0})</button>
        {collections.map((c) => (
          <button key={c.id} onClick={() => setActive(c.id)} data-testid={`collection-chip-${c.id}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border ${active === c.id ? "bg-[#781826] text-white border-[#781826]" : "border-[#E8DFD1] text-[#605255] hover:border-[#C59B27]"}`}>
            {c.name} {c.count > 0 ? `(${c.count})` : ""}
          </button>
        ))}
        <button onClick={() => setManage(true)} data-testid="manage-collections-btn" className="px-3 py-1.5 rounded-full text-xs font-medium border border-dashed border-[#C59B27] text-[#9E7B1B] flex items-center gap-1">
          <FolderPlus className="w-3.5 h-3.5" /> Collections
        </button>
      </div>

      {!data ? <FullSpinner /> : data.results.length === 0 ? (
        <Empty icon={Star} title="No profiles match" message="Your shortlist is empty or no profiles match your filters. Try clearing filters or explore new matches." action={<a href="/app/matches" className="btn-primary">Explore Matches</a>} />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="workspace-cards">
          {data.results.map((p) => <WorkspaceCard key={p.user_id} p={p} collections={collections} onChange={() => { load(); loadCols(); onChange && onChange(); }} />)}
        </div>
      )}

      {manage && <ManageCollections collections={collections} onClose={() => setManage(false)} onChange={() => { loadCols(); load(); }} />}
      {codeResult && <CodeResultDialog profile={codeResult} onClose={() => setCodeResult(null)} onChange={() => { load(); loadCols(); }} />}
    </div>
  );
}

function SaveSearchInline({ onSave, onCancel }) {
  const [name, setName] = useState("");
  return (
    <div className="flex items-center gap-1.5">
      <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Name this search" data-testid="save-search-input" className="border border-[#E8DFD1] rounded-lg px-2 py-1.5 text-xs w-40" />
      <button onClick={() => name.trim() && onSave(name.trim())} data-testid="confirm-save-search" className="btn-primary text-xs px-2 py-1.5"><Check className="w-3.5 h-3.5" /></button>
      <button onClick={onCancel} className="text-[#605255]"><X className="w-3.5 h-3.5" /></button>
    </div>
  );
}

function CodeResultDialog({ profile, onClose, onChange }) {
  const nav = useNavigate();
  const [added, setAdded] = useState(profile.shortlisted);
  const addShortlist = async () => { try { await api.post(`/shortlist/${profile.user_id}`); setAdded(true); toast.success("Added to shortlist"); onChange && onChange(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="code-result-dialog">
        <DialogHeader><DialogTitle className="font-serif text-[#58111A] flex items-center gap-2"><IdCard className="w-4 h-4 text-[#C59B27]" /> Profile {profile.profile_code}</DialogTitle></DialogHeader>
        <div className="flex gap-3 items-center">
          <Avatar photos={profile.photos} name={profile.full_name} size={64} className="rounded-xl w-16 h-16" />
          <div>
            <div className="font-serif text-lg text-[#58111A]">{profile.full_name}{profile.age ? `, ${profile.age}` : ""}</div>
            <div className="text-xs text-[#605255]">{[profile.city, profile.occupation].filter(Boolean).join(" · ")}</div>
            {profile.compatibility?.score != null && <div className="text-xs text-[#9E7B1B] mt-1">{profile.compatibility.score}% compatibility</div>}
          </div>
        </div>
        <DialogFooter className="gap-2">
          <button onClick={() => nav(`/app/profile/${profile.user_id}`)} className="text-sm px-4 py-2 rounded-xl border border-[#E8DFD1] text-[#605255]">View profile</button>
          {added ? <span className="text-sm px-4 py-2 rounded-xl bg-[#EEF6EE] text-emerald-700">In shortlist</span>
            : <button onClick={addShortlist} data-testid="code-add-shortlist" className="btn-primary text-sm">Add to shortlist</button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------ (was Shortlisted) */

function ManageCollections({ collections, onClose, onChange }) {
  const [name, setName] = useState("");
  const [editing, setEditing] = useState(null);
  const create = async () => {
    if (!name.trim()) return;
    try { await api.post("/jodi/collections", { name }); setName(""); onChange(); toast.success("Collection created"); } catch (e) { toast.error(apiError(e)); }
  };
  const rename = async (c) => {
    try { await api.put(`/jodi/collections/${c.id}`, { name: c.name }); setEditing(null); onChange(); toast.success("Renamed"); } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/jodi/collections/${id}`); onChange(); toast.success("Collection deleted"); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="manage-collections-dialog">
        <DialogHeader><DialogTitle className="font-serif text-[#58111A]">Private Collections</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {collections.map((c) => (
            <div key={c.id} className="flex items-center gap-2">
              {editing === c.id ? (
                <input autoFocus defaultValue={c.name} onChange={(e) => (c.name = e.target.value)} className="flex-1 border border-[#E8DFD1] rounded-lg px-2 py-1.5 text-sm" />
              ) : (
                <span className="flex-1 text-sm text-[#261A1C]">{c.name} {c.is_default && <span className="text-[10px] text-[#9E7B1B]">· default</span>}</span>
              )}
              {editing === c.id ? (
                <button onClick={() => rename(c)} className="text-emerald-600"><Check className="w-4 h-4" /></button>
              ) : (
                <button onClick={() => setEditing(c.id)} data-testid={`rename-${c.id}`} className="text-[#605255] hover:text-[#781826]"><Pencil className="w-3.5 h-3.5" /></button>
              )}
              {!c.is_default && <button onClick={() => remove(c.id)} data-testid={`delete-col-${c.id}`} className="text-[#605255] hover:text-[#BE123C]"><Trash2 className="w-3.5 h-3.5" /></button>}
            </div>
          ))}
        </div>
        <div className="flex gap-2 pt-2 border-t border-[#F1E9DA]">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="New collection name" data-testid="new-collection-input" className="flex-1 border border-[#E8DFD1] rounded-lg px-3 py-2 text-sm" />
          <button onClick={create} data-testid="create-collection-btn" className="btn-primary text-sm px-3"><Plus className="w-4 h-4" /></button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------ Workspace card */
function WorkspaceCard({ p, collections, onChange }) {
  const nav = useNavigate();
  const { flags = {} } = useAuth();
  const [busy, setBusy] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [famOpen, setFamOpen] = useState(false);
  const [meetOpen, setMeetOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const active = timeAgo(p.last_active);
  const na = p.next_action;

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); if (msg) toast.success(msg); onChange && onChange(); refreshAttention(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const removeShortlist = () => act(() => api.post(`/shortlist/${p.user_id}`), "Removed from shortlist");
  const sendInterest = () => act(() => api.post("/interests/send", { to_user: p.user_id }), "Interest sent");
  const acceptInterest = () => act(() => api.post(`/interests/accept/${p.their_interest_id}`), "Interest accepted");
  const requestContact = () => act(() => api.post(`/requests/contact/${p.user_id}`), "Contact request sent");
  const toggleCollection = (cid, has) => act(() => has ? api.delete(`/jodi/collections/${cid}/profiles/${p.user_id}`) : api.post(`/jodi/collections/${cid}/profiles/${p.user_id}`), has ? "Removed" : "Added to collection");

  return (
    <div className="mj-card p-4 flex flex-col gap-3 animate-fadeup" data-testid={`ws-card-${p.user_id}`}>
      <div className="flex gap-3">
        <div className="relative shrink-0 cursor-pointer" onClick={() => nav(`/app/profile/${p.user_id}`)}>
          <Avatar photos={p.photos} name={p.full_name} size={72} className="rounded-xl w-[72px] h-[72px]" />
          {p.premium && <span className="absolute -top-1 -right-1 bg-[#C59B27] text-[#3B0A12] text-[8px] font-bold px-1.5 py-0.5 rounded-full">PREM</span>}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-serif text-base font-semibold text-slate-900 leading-tight cursor-pointer hover:text-[#781826]" onClick={() => nav(`/app/profile/${p.user_id}`)}>
              {p.full_name}{p.age ? `, ${p.age}` : ""}
            </h3>
            {p.compatibility?.score != null && (
              <span className="shrink-0 bg-[#3B0A12] text-[#E2C382] text-[10px] px-2 py-0.5 rounded-full font-semibold flex items-center gap-1"><Sparkles className="w-3 h-3" />{p.compatibility.score}%</span>
            )}
          </div>
          <div className="mt-1 space-y-0.5 text-[11px] text-[#605255]">
            {(p.city || p.state) && <div className="flex items-center gap-1"><MapPin className="w-3 h-3 text-[#C59B27]" />{[p.city, p.state].filter(Boolean).join(", ")}</div>}
            {p.occupation && <div className="flex items-center gap-1"><Briefcase className="w-3 h-3 text-[#C59B27]" />{p.occupation}</div>}
            {p.qualification && <div className="flex items-center gap-1"><GraduationCap className="w-3 h-3 text-[#C59B27]" />{p.qualification}</div>}
          </div>
          <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
            <span className="text-[9px] font-mono text-[#9E7B1B]">{p.profile_code}</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#FAF3E0] text-[#9E7B1B] font-medium">{STAGE_LABEL[p.journey_stage] || "Shortlisted"}</span>
            {active && <span className="text-[10px] text-emerald-600">{active}</span>}
            {(p.verification || {}).identity && <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />}
          </div>
        </div>
      </div>

      {/* collection chips */}
      {(p.collections || []).length > 0 && (
        <div className="flex flex-wrap gap-1">
          {p.collections.map((cid) => {
            const c = collections?.find((x) => x.id === cid);
            return c ? <span key={cid} className="text-[10px] px-2 py-0.5 rounded-full bg-[#EEF6EE] text-emerald-700 border border-emerald-100">{c.name}</span> : null;
          })}
        </div>
      )}

      {/* next action pill */}
      {na && na.code !== "REVIEW" && (
        <div className={`flex items-center gap-1.5 text-[11px] rounded-lg px-2.5 py-1.5 ${p.needs_attention ? "bg-[#FBEBE1] text-[#BE123C]" : "bg-[#F5F0E6] text-[#9E7B1B]"}`}>
          <ChevronRight className="w-3.5 h-3.5" /> {na.label}
        </div>
      )}

      {/* actions */}
      <div className="mt-auto pt-2 border-t border-[#F1E9DA] flex items-center gap-2">
        {p.their_interest_status === "pending" ? (
          <button disabled={busy} onClick={acceptInterest} data-testid={`accept-${p.user_id}`} className="btn-primary flex-1 text-xs py-2"><Heart className="w-3.5 h-3.5" /> Accept</button>
        ) : p.connected ? (
          <button disabled={busy} onClick={() => nav(`/app/messages/${p.user_id}`)} data-testid={`chat-${p.user_id}`} className="btn-primary flex-1 text-xs py-2"><MessagesSquare className="w-3.5 h-3.5" /> Chat{p.chat?.unread ? ` (${p.chat.unread})` : ""}</button>
        ) : p.interest_status === "pending" ? (
          <button disabled className="flex-1 text-xs py-2 rounded-xl bg-[#F5F0E6] text-[#9E7B1B] font-medium">Interest Sent</button>
        ) : (
          <button disabled={busy} onClick={sendInterest} data-testid={`interest-${p.user_id}`} className="btn-primary flex-1 text-xs py-2"><Heart className="w-3.5 h-3.5" /> Interest</button>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid={`more-${p.user_id}`} className="w-9 h-9 rounded-xl border border-[#E8DFD1] text-[#605255] hover:border-[#C59B27] flex items-center justify-center"><MoreHorizontal className="w-4 h-4" /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onClick={() => nav(`/app/profile/${p.user_id}`)}>View full profile</DropdownMenuItem>
            {flags.CONTACT_REQUESTS !== false && !p.contact_request && p.connected && (
              <DropdownMenuItem onClick={requestContact} data-testid={`req-contact-${p.user_id}`}>Request contact</DropdownMenuItem>
            )}
            {p.connected && <DropdownMenuItem onClick={() => nav(`/app/jodi/understanding/${p.user_id}`)} data-testid={`understand-${p.user_id}`}>Understand each other</DropdownMenuItem>}
            {p.connected && <DropdownMenuItem onClick={() => setMeetOpen(true)} data-testid={`plan-meeting-${p.user_id}`}>Plan a meeting</DropdownMenuItem>}
            {flags.FAMILY_CONNECT !== false && <DropdownMenuItem onClick={() => setFamOpen(true)} data-testid={`family-${p.user_id}`}>{p.family_review?.shared ? "Manage family review" : "Share for family review"}</DropdownMenuItem>}
            <DropdownMenuItem onClick={() => setNoteOpen(true)} data-testid={`note-${p.user_id}`}>{p.has_note ? "Edit private note" : "Add private note"}</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setAiOpen(true)} data-testid={`ai-${p.user_id}`}>AI profile summary</DropdownMenuItem>
            <DropdownMenuSeparator />
            <div className="px-2 py-1 text-[10px] text-[#9E7B1B] uppercase tracking-wide">Collections</div>
            {(collections || []).map((c) => {
              const has = (p.collections || []).includes(c.id);
              return <DropdownMenuItem key={c.id} onClick={() => toggleCollection(c.id, has)}>{has ? <Check className="w-3.5 h-3.5 mr-2 text-emerald-600" /> : <span className="w-3.5 mr-2" />}{c.name}</DropdownMenuItem>;
            })}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={removeShortlist} className="text-[#BE123C]">Remove from shortlist</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {noteOpen && <NoteDialog target={p.user_id} name={p.full_name} onClose={() => setNoteOpen(false)} onSaved={onChange} />}
      {famOpen && <FamilyDialog target={p.user_id} name={p.full_name} current={p.family_review} onClose={() => setFamOpen(false)} onSaved={onChange} />}
      {meetOpen && <MeetingDialog target={p.user_id} name={p.full_name} onClose={() => setMeetOpen(false)} onSaved={onChange} />}
      {aiOpen && <AiSummaryDialog target={p.user_id} name={p.full_name} onClose={() => setAiOpen(false)} />}
    </div>
  );
}

/* ------------------------------------------------------ dialogs */
function NoteDialog({ target, name, onClose, onSaved }) {
  const [text, setText] = useState("");
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { api.get(`/notes/${target}`).then(({ data }) => { setText(data.note || ""); setLoaded(true); }).catch(() => setLoaded(true)); }, [target]);
  const save = async () => { try { await api.put(`/notes/${target}`, { text }); toast.success("Note saved"); onSaved && onSaved(); onClose(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="note-dialog">
        <DialogHeader><DialogTitle className="font-serif text-[#58111A]">Private note · {name}</DialogTitle></DialogHeader>
        {!loaded ? <FullSpinner /> : (
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={6} data-testid="note-textarea"
            placeholder="e.g. Ask about relocation plans. Family prefers to meet in person." className="w-full border border-[#E8DFD1] rounded-xl p-3 text-sm" />
        )}
        <p className="text-[11px] text-[#9E7B1B]">Only you can see this note.</p>
        <DialogFooter><button onClick={save} data-testid="save-note-btn" className="btn-primary text-sm">Save note</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FamilyDialog({ target, name, current, onClose, onSaved }) {
  const [fields, setFields] = useState(current?.fields || ["basic"]);
  const shared = current?.shared;
  const toggle = (k) => setFields((f) => f.includes(k) ? f.filter((x) => x !== k) : [...f, k]);
  const share = async () => { try { await api.post(`/jodi/family-review/${target}`, { share: true, fields }); toast.success("Shared for family review"); onSaved && onSaved(); onClose(); } catch (e) { toast.error(apiError(e)); } };
  const stop = async () => { try { await api.post(`/jodi/family-review/${target}`, { share: false, fields: [] }); toast.success("Sharing stopped"); onSaved && onSaved(); onClose(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="family-dialog">
        <DialogHeader><DialogTitle className="font-serif text-[#58111A]">Family review · {name}</DialogTitle></DialogHeader>
        <p className="text-xs text-[#605255]">Choose what to include when you discuss this profile with your family. Private notes, chats and contact details are never shared.</p>
        <div className="grid grid-cols-2 gap-2 py-1">
          {FAMILY_FIELDS.map((f) => (
            <label key={f.key} className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={fields.includes(f.key)} onChange={() => toggle(f.key)} data-testid={`fam-field-${f.key}`} className="w-4 h-4 accent-[#781826]" /> {f.label}
            </label>
          ))}
        </div>
        <DialogFooter className="gap-2">
          {shared && <button onClick={stop} data-testid="stop-sharing-btn" className="text-sm px-4 py-2 rounded-xl border border-[#E8DFD1] text-[#BE123C]">Stop sharing</button>}
          <button onClick={share} data-testid="share-family-btn" className="btn-primary text-sm">{shared ? "Update sharing" : "Share with family"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MeetingDialog({ target, name, onClose, onSaved }) {
  const { flags = {} } = useAuth();
  const [type, setType] = useState("coffee");
  const [venue, setVenue] = useState("");
  const [when, setWhen] = useState("");
  const [note, setNote] = useState("");
  const [family, setFamily] = useState([]);
  const [trusted, setTrusted] = useState("");
  useEffect(() => { if (flags.FAMILY_CONNECT !== false) api.get("/family").then(({ data }) => setFamily(data.members || [])).catch(() => {}); }, [flags.FAMILY_CONNECT]);
  const plan = async () => {
    try {
      await api.post(`/jodi/meetings/plan/${target}`, { meeting_type: type, venue, scheduled_at: when || null, note, trusted_contact_id: trusted || null });
      toast.success("Meeting proposed"); onSaved && onSaved(); onClose();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="meeting-dialog">
        <DialogHeader><DialogTitle className="font-serif text-[#58111A]">Plan a safe meeting · {name}</DialogTitle></DialogHeader>
        <label className="text-xs text-[#605255]">Meeting type</label>
        <div className="flex flex-wrap gap-1.5">
          {MEETING_TYPES.map((m) => (
            <button key={m.key} onClick={() => setType(m.key)} data-testid={`meet-type-${m.key}`}
              className={`text-xs px-2.5 py-1.5 rounded-full border ${type === m.key ? "bg-[#781826] text-white border-[#781826]" : "border-[#E8DFD1] text-[#605255]"}`}>{m.label}</button>
          ))}
        </div>
        <input value={venue} onChange={(e) => setVenue(e.target.value)} placeholder="Suggested public venue" data-testid="meet-venue" className="w-full border border-[#E8DFD1] rounded-xl px-3 py-2 text-sm" />
        <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} data-testid="meet-when" className="w-full border border-[#E8DFD1] rounded-xl px-3 py-2 text-sm" />
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Optional note" className="w-full border border-[#E8DFD1] rounded-xl p-3 text-sm" />
        {flags.FAMILY_CONNECT !== false && (
          <div>
            <label className="text-xs text-[#605255] flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5 text-emerald-600" /> Trusted contact heads-up (optional)</label>
            <select value={trusted} onChange={(e) => setTrusted(e.target.value)} data-testid="meet-trusted" className="w-full mt-1 border border-[#E8DFD1] rounded-xl px-3 py-2 text-sm">
              <option value="">Don't notify anyone</option>
              {family.map((m) => <option key={m.id} value={m.id}>{m.name}{m.relation ? ` (${m.relation})` : ""}</option>)}
            </select>
            {family.length === 0 && <p className="text-[10px] text-[#9E7B1B] mt-1">Add family in Family Connect to enable trusted-contact heads-ups.</p>}
          </div>
        )}
        <p className="text-[11px] text-[#9E7B1B]">Always meet in a public place. Your safety comes first.</p>
        <DialogFooter><button onClick={plan} data-testid="propose-meeting-btn" className="btn-primary text-sm">Propose meeting</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AiSummaryDialog({ target, name, onClose }) {
  const [text, setText] = useState(null);
  useEffect(() => { api.get(`/jodi/ai/summary/${target}`).then(({ data }) => setText(data.summary)).catch(() => setText("Summary unavailable right now.")); }, [target]);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="ai-summary-dialog">
        <DialogHeader><DialogTitle className="font-serif text-[#58111A] flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#C59B27]" /> Summary · {name}</DialogTitle></DialogHeader>
        {text === null ? <FullSpinner label="Summarising…" /> : <p className="text-sm text-[#261A1C] leading-relaxed">{text}</p>}
        <p className="text-[11px] text-[#9E7B1B]">A neutral, factual summary. It does not rank or decide for you.</p>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------ Compare */
function CompareTab() {
  const { flags = {} } = useAuth();
  const [pool, setPool] = useState(null);
  const [sel, setSel] = useState([]);
  const [result, setResult] = useState(null);

  useEffect(() => { api.get("/jodi/workspace/profiles").then(({ data }) => setPool(data.results)).catch(() => setPool([])); }, []);
  const toggle = (id) => setSel((s) => s.includes(id) ? s.filter((x) => x !== id) : s.length >= 4 ? s : [...s, id]);
  const run = async () => {
    try { const { data } = await api.get(`/jodi/workspace/compare?ids=${sel.join(",")}`); setResult(data.profiles); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (flags.MATCH_COMPARISON === false) return <Empty icon={GitCompare} title="Comparison unavailable" message="Profile comparison is currently turned off." />;
  if (!pool) return <FullSpinner />;
  if (pool.length < 2) return <Empty icon={GitCompare} title="Add profiles to compare" message="Shortlist at least 2 profiles to compare them side by side." action={<a href="/app/matches" className="btn-primary">Explore Matches</a>} />;

  const ROWS = [
    ["Compatibility", (p) => `${p.compatibility?.score ?? "—"}%`],
    ["Age", (p) => p.basic.age || "—"], ["Height", (p) => p.basic.height || "—"],
    ["Location", (p) => [p.basic.city, p.basic.state].filter(Boolean).join(", ") || "—"],
    ["Religion", (p) => p.basic.religion || "—"], ["Community", (p) => p.basic.community || "—"],
    ["Mother tongue", (p) => p.basic.mother_tongue || "—"], ["Marital status", (p) => p.basic.marital_status || "—"],
    ["Education", (p) => p.education_career.qualification || "—"], ["Profession", (p) => p.education_career.occupation || "—"],
    ["Income", (p) => p.education_career.income_range || "—"], ["Diet", (p) => p.lifestyle.diet || "—"],
    ["Family type", (p) => p.family.family_type || "—"],
    ["Verified", (p) => (p.trust.verification || {}).identity ? "Yes" : "No"],
    ["Profile complete", (p) => `${p.trust.completion ?? 0}%`],
    ["Interest", (p) => p.interaction.interest_status || "—"], ["Connected", (p) => p.interaction.connected ? "Yes" : "No"],
    ["Contact", (p) => p.interaction.contact_status || "—"], ["Meeting", (p) => p.interaction.meeting_status || "—"],
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-[#605255]">Select 2–4 profiles ({sel.length} selected).</p>
        <button disabled={sel.length < 2} onClick={run} data-testid="run-compare-btn" className="btn-primary text-sm disabled:opacity-50">Compare</button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
        {pool.map((p) => (
          <button key={p.user_id} onClick={() => toggle(p.user_id)} data-testid={`compare-pick-${p.user_id}`}
            className={`flex items-center gap-2 p-2 rounded-xl border text-left ${sel.includes(p.user_id) ? "border-[#781826] bg-[#FBEBE1]" : "border-[#E8DFD1]"}`}>
            <Avatar photos={p.photos} name={p.full_name} size={32} className="rounded-lg w-8 h-8" />
            <span className="text-[11px] font-medium text-[#261A1C] truncate">{p.full_name}</span>
          </button>
        ))}
      </div>

      {result && (
        <div className="overflow-x-auto mj-card p-0" data-testid="compare-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#FAF3E0]">
                <th className="text-left p-3 font-medium text-[#9E7B1B]">Factor</th>
                {result.map((p) => (
                  <th key={p.user_id} className="p-3 text-center min-w-[130px]">
                    <Avatar photos={p.photos} name={p.full_name} size={40} className="rounded-full w-10 h-10 mx-auto mb-1" />
                    <div className="font-serif text-sm text-[#58111A]">{p.full_name}</div>
                    <VerifiedBadges verification={p.trust?.verification} compact />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map(([label, fn], i) => (
                <tr key={label} className={i % 2 ? "bg-[#FDFBF7]" : "bg-white"}>
                  <td className="p-3 text-[#605255] font-medium">{label}</td>
                  {result.map((p) => <td key={p.user_id} className="p-3 text-center text-[#261A1C]">{fn(p)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[11px] text-[#9E7B1B] p-3">These are facts side by side. There is no "winner" — the choice is yours.</p>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------ Conversations */
function ConversationsTab() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/conversations").then(({ data }) => setData(data.conversations)).catch(() => setData([])); }, []);
  if (!data) return <FullSpinner />;
  if (data.length === 0) return <Empty icon={MessagesSquare} title="No conversations yet" message="Your conversations will appear here when you connect with someone." />;
  return (
    <div className="space-y-2" data-testid="conversations-list">
      {data.map((c) => (
        <button key={c.id} onClick={() => nav(`/app/messages/${c.other.user_id}`)} data-testid={`conv-${c.other.user_id}`}
          className="w-full flex items-center gap-3 mj-card p-3 text-left hover:border-[#C59B27]">
          <Avatar photos={c.other.photos} name={c.other.full_name} size={48} className="rounded-full w-12 h-12" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between">
              <span className="font-serif font-semibold text-[#58111A]">{c.other.full_name}</span>
              {c.unread > 0 && <span className="text-[10px] bg-[#BE123C] text-white rounded-full px-2 py-0.5">{c.unread}</span>}
            </div>
            <p className="text-xs text-[#605255] truncate">{c.last_message || "Say hello"}</p>
          </div>
        </button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------ Contact status */
function ContactStatusTab({ onChange }) {
  const [data, setData] = useState(null);
  const load = () => api.get("/requests").then(({ data }) => setData(data)).catch(() => setData({ incoming: [], outgoing: [] }));
  useEffect(() => { load(); }, []);
  const respond = async (id, action) => { try { await api.post(`/requests/${id}/${action}`); toast.success(`Request ${action}ed`); load(); onChange && onChange(); refreshAttention(); } catch (e) { toast.error(apiError(e)); } };
  if (!data) return <FullSpinner />;
  const inc = (data.incoming || []).filter((r) => r.kind === "contact");
  const out = (data.outgoing || []).filter((r) => r.kind === "contact");
  if (inc.length === 0 && out.length === 0) return <Empty icon={PhoneCall} title="No contact requests" message="Contact requests you send or receive will be tracked here with their status." />;
  const Row = ({ r, incoming }) => (
    <div className="flex items-center gap-3 mj-card p-3" data-testid={`contact-${r.id}`}>
      <Avatar photos={r.profile?.photos} name={r.profile?.full_name} size={44} className="rounded-full w-11 h-11" />
      <div className="flex-1 min-w-0">
        <div className="font-serif font-semibold text-[#58111A]">{r.profile?.full_name || "Member"}</div>
        <span className={`text-[11px] px-2 py-0.5 rounded-full ${r.status === "accepted" ? "bg-emerald-50 text-emerald-700" : r.status === "declined" ? "bg-rose-50 text-rose-600" : "bg-amber-50 text-amber-700"}`}>{r.status}</span>
      </div>
      {incoming && r.status === "pending" && (
        <div className="flex gap-1.5">
          <button onClick={() => respond(r.id, "accept")} data-testid={`accept-contact-${r.id}`} className="btn-primary text-xs px-3 py-1.5">Accept</button>
          <button onClick={() => respond(r.id, "decline")} className="text-xs px-3 py-1.5 rounded-lg border border-[#E8DFD1] text-[#605255]">Decline</button>
        </div>
      )}
    </div>
  );
  return (
    <div className="space-y-4" data-testid="contact-status">
      {inc.length > 0 && <div><h3 className="font-serif text-lg font-semibold text-[#58111A] mb-2">Received</h3><div className="space-y-2">{inc.map((r) => <Row key={r.id} r={r} incoming />)}</div></div>}
      {out.length > 0 && <div><h3 className="font-serif text-lg font-semibold text-[#58111A] mb-2">Sent</h3><div className="space-y-2">{out.map((r) => <Row key={r.id} r={r} />)}</div></div>}
    </div>
  );
}

/* ------------------------------------------------------ Family review */
function FamilyTab({ onChange }) {
  const [data, setData] = useState(null);
  const [collections, setCollections] = useState([]);
  const load = () => api.get("/jodi/family-review").then(({ data }) => setData(data)).catch(() => setData({ results: [] }));
  useEffect(() => { load(); api.get("/jodi/collections").then(({ data }) => setCollections(data.collections)).catch(() => {}); }, []);
  if (!data) return <FullSpinner />;
  if (data.results.length === 0) return <Empty icon={Users} title="No profiles shared with family" message="Profiles you share with family will appear here. Use “Share for family review” on any shortlisted profile." />;
  return (
    <div className="space-y-4" data-testid="family-review">
      {data.family_members?.length === 0 && (
        <div className="mj-card p-3 text-xs text-[#9E7B1B] flex items-center gap-2"><Users className="w-4 h-4" /> Tip: invite family in Family Connect so they can help you review.</div>
      )}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {data.results.map((p) => (
          <div key={p.user_id} className="space-y-2">
            <WorkspaceCard p={p} collections={collections} onChange={() => { load(); onChange && onChange(); }} />
            <div className="text-[11px] text-[#605255] px-1">Shared: {(p.family_fields || []).join(", ") || "basic"}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------ Meetings (Safe Meeting Center) */
const MEET_STATUS_STYLE = {
  accepted: "bg-emerald-50 text-emerald-700", declined: "bg-rose-50 text-rose-600",
  cancelled: "bg-slate-100 text-slate-500", completed: "bg-[#EAF6EA] text-emerald-800",
  rescheduled: "bg-amber-50 text-amber-700", proposed: "bg-amber-50 text-amber-700", pending: "bg-amber-50 text-amber-700",
};

function MeetingsTab({ onChange }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [reschedule, setReschedule] = useState(null);
  const load = () => api.get("/jodi/meetings").then(({ data }) => setData(data.meetings)).catch(() => setData([]));
  useEffect(() => { load(); }, []);
  const doAction = async (fn, msg) => { try { await fn(); toast.success(msg); load(); onChange && onChange(); refreshAttention(); } catch (e) { toast.error(apiError(e)); } };
  const respond = (id, action) => doAction(() => api.post(`/jodi/meetings/${id}/respond`, { action }), `Meeting ${action}ed`);
  const cancel = (id) => doAction(() => api.post(`/jodi/meetings/${id}/cancel`), "Meeting cancelled");
  const complete = (id) => doAction(() => api.post(`/jodi/meetings/${id}/complete`), "Marked completed");

  if (!data) return <FullSpinner />;
  if (data.length === 0) return <Empty icon={CalendarClock} title="No meetings planned" message="Your planned meetings will appear here. Use “Plan a meeting” on a connected profile." />;
  return (
    <div className="space-y-3" data-testid="meetings-list">
      {data.map((m) => {
        const st = m.meta?.status || m.status;
        const finished = ["cancelled", "declined", "completed"].includes(st);
        return (
          <div key={m.id} className="mj-card p-4" data-testid={`meeting-${m.id}`}>
            <div className="flex items-start gap-3">
              <Avatar photos={m.profile?.photos} name={m.profile?.full_name} size={48} className="rounded-full w-12 h-12 cursor-pointer shrink-0" onClick={() => m.profile && nav(`/app/profile/${m.profile.user_id}`)} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-serif font-semibold text-[#58111A]">{m.profile?.full_name || "Member"}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full capitalize ${MEET_STATUS_STYLE[st] || "bg-amber-50 text-amber-700"}`}>{st}</span>
                </div>
                <div className="text-[11px] text-[#605255] capitalize mt-0.5">
                  {(m.meta?.meeting_type || "meeting")}{m.meta?.venue ? ` · ${m.meta.venue}` : ""}{m.meta?.scheduled_at ? ` · ${new Date(m.meta.scheduled_at).toLocaleString()}` : ""}
                </div>
                {m.meta?.trusted_contact && <div className="text-[10px] text-emerald-700 mt-0.5 flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Trusted contact notified: {m.meta.trusted_contact.name}</div>}
                {m.awaiting_me && <div className="text-[11px] text-[#BE123C] mt-1">Awaiting your response</div>}

                {!finished && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {m.can_respond && (
                      <>
                        <button onClick={() => respond(m.id, "accept")} data-testid={`accept-meeting-${m.id}`} className="btn-primary text-xs px-3 py-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> Accept</button>
                        <button onClick={() => respond(m.id, "decline")} className="text-xs px-3 py-1.5 rounded-lg border border-[#E8DFD1] text-[#605255]"><XCircle className="w-3.5 h-3.5" /> Decline</button>
                      </>
                    )}
                    <button onClick={() => setReschedule(m)} data-testid={`reschedule-${m.id}`} className="text-xs px-3 py-1.5 rounded-lg border border-[#E8DFD1] text-[#9E7B1B] flex items-center gap-1"><RefreshCw className="w-3.5 h-3.5" /> Reschedule</button>
                    {st === "accepted" && <button onClick={() => complete(m.id)} data-testid={`complete-${m.id}`} className="text-xs px-3 py-1.5 rounded-lg border border-emerald-200 text-emerald-700 flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Mark met</button>}
                    <button onClick={() => cancel(m.id)} data-testid={`cancel-meeting-${m.id}`} className="text-xs px-3 py-1.5 rounded-lg border border-[#E8DFD1] text-[#BE123C]">Cancel</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
      {reschedule && <RescheduleDialog meeting={reschedule} onClose={() => setReschedule(null)} onSaved={() => { load(); onChange && onChange(); }} />}
    </div>
  );
}

function RescheduleDialog({ meeting, onClose, onSaved }) {
  const [when, setWhen] = useState(meeting.meta?.scheduled_at || "");
  const [venue, setVenue] = useState(meeting.meta?.venue || "");
  const save = async () => {
    try { await api.post(`/jodi/meetings/${meeting.id}/reschedule`, { scheduled_at: when || null, venue }); toast.success("New time proposed"); onSaved(); onClose(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="reschedule-dialog">
        <DialogHeader><DialogTitle className="font-serif text-[#58111A]">Propose a new time</DialogTitle></DialogHeader>
        <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} data-testid="reschedule-when" className="w-full border border-[#E8DFD1] rounded-xl px-3 py-2 text-sm" />
        <input value={venue} onChange={(e) => setVenue(e.target.value)} placeholder="Venue" data-testid="reschedule-venue" className="w-full border border-[#E8DFD1] rounded-xl px-3 py-2 text-sm" />
        <p className="text-[11px] text-[#9E7B1B]">Your connection will be asked to confirm the new time.</p>
        <DialogFooter><button onClick={save} data-testid="save-reschedule-btn" className="btn-primary text-sm">Send new time</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------ Notes & reminders */
function NotesRemindersTab() {
  const [rem, setRem] = useState(null);
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");
  const load = () => api.get("/jodi/reminders").then(({ data }) => setRem(data.reminders)).catch(() => setRem([]));
  useEffect(() => { load(); }, []);
  const add = async () => { if (!title.trim()) return; try { await api.post("/jodi/reminders", { title, due_at: due || null }); setTitle(""); setDue(""); load(); toast.success("Reminder added"); } catch (e) { toast.error(apiError(e)); } };
  const toggle = async (r) => { try { await api.put(`/jodi/reminders/${r.id}`, { done: !r.done }); load(); } catch (e) { toast.error(apiError(e)); } };
  const del = async (id) => { try { await api.delete(`/jodi/reminders/${id}`); load(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className="space-y-4" data-testid="notes-reminders">
      <div className="mj-card p-4">
        <h3 className="font-serif text-lg font-semibold text-[#58111A] mb-3">Add a reminder</h3>
        <div className="flex flex-col sm:flex-row gap-2">
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Follow up with Diya tomorrow" data-testid="reminder-input" className="flex-1 border border-[#E8DFD1] rounded-xl px-3 py-2 text-sm" />
          <input type="datetime-local" value={due} onChange={(e) => setDue(e.target.value)} data-testid="reminder-due" className="border border-[#E8DFD1] rounded-xl px-3 py-2 text-sm" />
          <button onClick={add} data-testid="add-reminder-btn" className="btn-primary text-sm"><Plus className="w-4 h-4" /> Add</button>
        </div>
      </div>
      {!rem ? <FullSpinner /> : rem.length === 0 ? (
        <Empty icon={Bell} title="No reminders yet" message="Add reminders to follow up, prepare for a meeting, or discuss a profile with family." />
      ) : (
        <div className="space-y-2">
          {rem.map((r) => (
            <div key={r.id} className={`mj-card p-3 flex items-center gap-3 ${r.done ? "opacity-60" : ""}`} data-testid={`reminder-${r.id}`}>
              <button onClick={() => toggle(r)} data-testid={`toggle-reminder-${r.id}`} className={`w-6 h-6 rounded-full border flex items-center justify-center ${r.done ? "bg-emerald-500 border-emerald-500 text-white" : "border-[#C59B27] text-transparent"}`}><Check className="w-4 h-4" /></button>
              <div className="flex-1 min-w-0">
                <div className={`text-sm text-[#261A1C] ${r.done ? "line-through" : ""}`}>{r.title}</div>
                {(r.due_at || r.target_name) && <div className="text-[11px] text-[#9E7B1B]">{r.target_name ? `${r.target_name} · ` : ""}{r.due_at ? new Date(r.due_at).toLocaleString() : ""}</div>}
              </div>
              <button onClick={() => del(r.id)} className="text-[#605255] hover:text-[#BE123C]"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-[#9E7B1B]">To add a private note on a specific profile, open its card and choose “Add private note”.</p>
    </div>
  );
}

/* ------------------------------------------------------ Next actions */
function NextActionsTab({ onChange }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const load = () => api.get("/jodi/workspace/next-actions").then(({ data }) => setData(data.actions)).catch(() => setData([]));
  useEffect(() => { load(); }, []);
  const go = async (a) => {
    const code = a.action.code;
    if (code === "RESPOND_INTEREST" && a.their_interest_id) { try { await api.post(`/interests/accept/${a.their_interest_id}`); toast.success("Interest accepted"); load(); onChange && onChange(); refreshAttention(); } catch (e) { toast.error(apiError(e)); } return; }
    if (code === "REVIEW_CONTACT" && a.contact_request?.id) { nav("/app/requests"); return; }
    if (code === "CONTINUE_CHAT" || code === "START_CHAT") { nav(`/app/messages/${a.user_id}`); return; }
    nav(`/app/profile/${a.user_id}`);
  };
  if (!data) return <FullSpinner />;
  if (data.length === 0) return <Empty icon={ListChecks} title="You're all caught up" message="Actionable next steps for the profiles you're considering will show up here." />;
  return (
    <div className="space-y-2" data-testid="next-actions">
      {data.map((a) => (
        <div key={a.user_id} className={`mj-card p-3 flex items-center gap-3 ${a.attention ? "border-[#C59B27]" : ""}`} data-testid={`action-${a.user_id}`}>
          <Avatar photos={a.photos} name={a.full_name} size={44} className="rounded-full w-11 h-11" />
          <div className="flex-1 min-w-0">
            <div className="font-serif font-semibold text-[#58111A]">{a.full_name}</div>
            <div className="text-xs text-[#605255]">{a.action.label}</div>
          </div>
          <button onClick={() => go(a)} data-testid={`do-action-${a.user_id}`} className="btn-primary text-xs px-3 py-1.5">Do it <ChevronRight className="w-3.5 h-3.5" /></button>
        </div>
      ))}
    </div>
  );
}
